<template>
  <div class="min-h-screen bg-grey flex items-center justify-center p-4">
    <!-- Animated Background Elements -->
    <div class="absolute inset-0 overflow-hidden">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-white rounded-full opacity-10 animate-pulse"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-white rounded-full opacity-10 animate-pulse delay-1000"
      ></div>
      <div
        class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white rounded-full opacity-5 animate-pulse delay-500"
      ></div>
    </div>

    <!-- Login Card -->
    <div
      class="bg-white rounded-2xl shadow-2xl w-full max-w-md relative z-10 overflow-hidden"
    >
      <!-- Header with Decoration -->
      <div class="bg-primary-50 px-8 py-6 text-white">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold">Nobel Lending</h1>
            <p class="text-sm opacity-90 mt-1">Verification System</p>
          </div>
          <div
            class="w-20 h-20 bg-white rounded-xl flex items-center justify-center"
          >
            <img :src="logo" />
          </div>
        </div>
        <div class="mt-0 flex space-x-1">
          <div class="h-1 w-12 bg-white rounded-full"></div>
          <div class="h-1 w-8 bg-white/50 rounded-full"></div>
          <div class="h-1 w-4 bg-white/30 rounded-full"></div>
        </div>
      </div>

      <!-- Login Form -->
      <div class="px-8 py-8">
        <!-- Welcome Message -->
        <div class="mb-8">
          <h2 class="text-2xl font-bold text-gray-900">Welcome Back!</h2>
          <p class="text-gray-500 mt-1">Please sign in to your account</p>
        </div>

        <!-- Error Alert -->
        <div
          v-if="authError"
          class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2 animate-shake"
        >
          <svg
            class="w-5 h-5 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span class="text-sm">{{ authError }}</span>
        </div>

        <!-- Login Form -->
        <form @submit.prevent="handleLogin" class="space-y-6">
          <!-- Email Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Email Address</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 12H8m0 0l4-4m-4 4l4 4m8-4a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <input
                v-model="loginForm.email"
                type="email"
                required
                placeholder="Enter your email"
                class="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :class="{
                  'border-red-300 focus:ring-red-500': validationErrors.email,
                }"
              />
            </div>
            <p v-if="validationErrors.email" class="mt-1 text-xs text-red-600">
              {{ validationErrors.email }}
            </p>
          </div>

          <!-- Password Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Password</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <input
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                required
                placeholder="Enter your password"
                class="block w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :class="{
                  'border-red-300 focus:ring-red-500':
                    validationErrors.password,
                }"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg
                  v-if="!showPassword"
                  class="h-5 w-5 text-gray-400 hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5 text-gray-400 hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              </button>
            </div>
            <p
              v-if="validationErrors.password"
              class="mt-1 text-xs text-red-600"
            >
              {{ validationErrors.password }}
            </p>
          </div>

          <!-- Remember Me & Forgot Password -->
          <div class="flex items-center justify-between">
            <!-- <label class="flex items-center">
              <input
                v-model="loginForm.remember"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span class="ml-2 text-sm text-gray-600">Remember me</span>
            </label>
            <button
              type="button"
              @click="forgotPassword"
              class="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Forgot Password?
            </button> -->
          </div>

          <!-- Login Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full bg-indigo-800 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center space-x-2"
          >
            <svg
              v-if="isLoading"
              class="animate-spin h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{{ isLoading ? "Signing in..." : "Sign In" }}</span>
          </button>
        </form>

        <!-- Sign Up Link -->
        <!-- <p class="mt-6 text-center text-sm text-gray-500">
          Don't have an account?
          <a
            href="#"
            @click.prevent="signUp"
            class="text-blue-600 hover:text-blue-800 font-medium"
            >Contact Administrator</a
          >
        </p> -->
      </div>

      <!-- Footer -->
      <div class="px-8 py-4 bg-gray-50 border-t border-gray-100">
        <p class="text-xs text-center text-gray-500">
          © {{ new Date().getFullYear() }} Nobel Lending. All rights reserved.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";

import logo from "@/assets/logo.png";

const router = useRouter();
const authStore = useAuthStore();

// Form state
const loginForm = reactive({
  email: "",
  password: "",
  remember: false,
});

// UI state
const showPassword = ref(false);
const isLoading = ref(false);
const authError = ref("");
const validationErrors = reactive({
  email: "",
  password: "",
});

// Handle login submission
const handleLogin = async () => {
  // Reset errors
  authError.value = "";
  validationErrors.email = "";
  validationErrors.password = "";

  // Basic validation
  if (!loginForm.email) {
    validationErrors.email = "Email is required";
    return;
  }
  if (!isValidEmail(loginForm.email)) {
    validationErrors.email = "Please enter a valid email address";
    return;
  }
  if (!loginForm.password) {
    validationErrors.password = "Password is required";
    return;
  }

  isLoading.value = true;

  try {
    // Attempt login
    const success = await authStore.login({
      email: loginForm.email,
      password: loginForm.password,
      remember: loginForm.remember,
    });

    if (success) {
      // Redirect to dashboard
      router.push("/admin/");
    } else {
      authError.value = "Invalid email or password";
    }
  } catch (error) {
    authError.value = error.message || "An error occurred during login";
  } finally {
    isLoading.value = false;
  }
};

// Email validation helper
const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Forgot password handler
// const forgotPassword = () => {
//   // In a real app, this would navigate to password reset page
//   // alert("Please contact your system administrator to reset your password.");
// };

// Sign up handler
</script>

<style scoped>
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-2px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(2px);
  }
}

.animate-shake {
  animation: shake 0.5s ease-in-out;
}

.delay-1000 {
  animation-delay: 1000ms;
}

.delay-500 {
  animation-delay: 500ms;
}
</style>
